import Type from "typebox";

const resourceId = (description: string) =>
  Type.String({
    description,
    format: "uuid",
    minLength: 36,
    maxLength: 36,
  });

export const TenantIdSchema = resourceId("Opaque tenant identifier.");
export const UserIdSchema = resourceId("Opaque user identifier.");
export const MembershipIdSchema = resourceId("Opaque membership identifier.");
export const InvitationIdSchema = resourceId("Opaque invitation identifier.");
export const SessionIdSchema = resourceId(
  "Opaque browser session identifier; never a session token.",
);
export const AuthChallengeIdSchema = resourceId("Opaque, short-lived login challenge identifier.");
export const ReauthenticationIdSchema = resourceId(
  "Opaque recent-reauthentication proof identifier.",
);
export const TotpCredentialIdSchema = resourceId("Opaque TOTP credential identifier.");
export const ApiKeyIdSchema = resourceId(
  "Opaque API key record identifier; never authentication material.",
);
export const AuditEventIdSchema = resourceId("Opaque append-only audit event identifier.");
export const JobIdSchema = resourceId("Opaque asynchronous job identifier.");
export const OperationIdSchema = resourceId("Opaque domain operation identifier.");

// Forward-compatible resource identifiers. Phase 1 exposes these as boundaries only;
// later phases attach domain schemas without changing identifier semantics.
export const ProjectIdSchema = resourceId("Opaque project identifier.");
export const EnvironmentIdSchema = resourceId("Opaque environment identifier.");
export const ServiceIdSchema = resourceId("Opaque service identifier.");
export const HostIdSchema = resourceId("Opaque host identifier.");
export const SourceSnapshotIdSchema = resourceId("Opaque source snapshot identifier.");
export const SourceConnectionIdSchema = resourceId("Opaque source provider connection identifier.");
export const UploadSessionIdSchema = resourceId("Opaque resumable upload session identifier.");
export const BuildIdSchema = resourceId("Opaque build identifier.");
export const ArtifactIdSchema = resourceId("Opaque immutable artifact identifier.");
export const ReleaseIdSchema = resourceId("Opaque immutable release identifier.");
export const DeploymentIdSchema = resourceId("Opaque deployment identifier.");
export const DomainIdSchema = resourceId("Opaque domain record identifier.");
export const ProviderIntegrationIdSchema = resourceId(
  "Opaque write-only provider integration identifier.",
);
export const DomainDnsRecordIdSchema = resourceId("Opaque managed domain DNS record identifier.");
export const CaddyRouteRevisionIdSchema = resourceId("Opaque Caddy route revision identifier.");
export const DatabaseIdSchema = resourceId("Opaque managed database identifier.");
export const DatabaseGrantEvidenceIdSchema = resourceId(
  "Opaque managed database grant evidence identifier.",
);
export const BackupArtifactIdSchema = resourceId("Opaque plaintext-isolated backup artifact identifier.");
export const RestoreAttemptIdSchema = resourceId("Opaque restore attempt identifier.");
export const SecretBindingIdSchema = resourceId("Opaque versioned secret binding identifier.");
export const SecretIdSchema = resourceId("Opaque write-only secret record identifier.");

export const RESOURCE_TYPES = [
  "tenant",
  "user",
  "membership",
  "invitation",
  "session",
  "api_key",
  "job",
  "operation",
  "project",
  "environment",
  "service",
  "host",
  "source_snapshot",
  "source_connection",
  "upload_session",
  "build",
  "release",
  "deployment",
  "domain",
  "provider_integration",
  "domain_dns_record",
  "caddy_route_revision",
  "database",
  "database_grant_evidence",
  "backup_artifact",
  "restore_attempt",
  "secret_binding",
  "secret",
] as const;

export const ResourceTypeSchema = Type.Union([
  Type.Literal("tenant"),
  Type.Literal("user"),
  Type.Literal("membership"),
  Type.Literal("invitation"),
  Type.Literal("session"),
  Type.Literal("api_key"),
  Type.Literal("job"),
  Type.Literal("operation"),
  Type.Literal("project"),
  Type.Literal("environment"),
  Type.Literal("service"),
  Type.Literal("host"),
  Type.Literal("source_snapshot"),
  Type.Literal("source_connection"),
  Type.Literal("upload_session"),
  Type.Literal("build"),
  Type.Literal("release"),
  Type.Literal("deployment"),
  Type.Literal("domain"),
  Type.Literal("provider_integration"),
  Type.Literal("domain_dns_record"),
  Type.Literal("caddy_route_revision"),
  Type.Literal("database"),
  Type.Literal("database_grant_evidence"),
  Type.Literal("backup_artifact"),
  Type.Literal("restore_attempt"),
  Type.Literal("secret_binding"),
  Type.Literal("secret"),
]);

export const ResourceReferenceSchema = Type.Object(
  {
    type: ResourceTypeSchema,
    id: Type.String({ format: "uuid", minLength: 36, maxLength: 36 }),
    label: Type.Optional(Type.String({ minLength: 1, maxLength: 160 })),
  },
  {
    $id: "ResourceReference",
    additionalProperties: false,
  },
);

export type TenantId = Type.Static<typeof TenantIdSchema>;
export type UserId = Type.Static<typeof UserIdSchema>;
export type MembershipId = Type.Static<typeof MembershipIdSchema>;
export type InvitationId = Type.Static<typeof InvitationIdSchema>;
export type SessionId = Type.Static<typeof SessionIdSchema>;
export type AuthChallengeId = Type.Static<typeof AuthChallengeIdSchema>;
export type ReauthenticationId = Type.Static<typeof ReauthenticationIdSchema>;
export type ApiKeyId = Type.Static<typeof ApiKeyIdSchema>;
export type AuditEventId = Type.Static<typeof AuditEventIdSchema>;
export type JobId = Type.Static<typeof JobIdSchema>;
export type OperationId = Type.Static<typeof OperationIdSchema>;
export type ProjectId = Type.Static<typeof ProjectIdSchema>;
export type EnvironmentId = Type.Static<typeof EnvironmentIdSchema>;
export type ServiceId = Type.Static<typeof ServiceIdSchema>;
export type HostId = Type.Static<typeof HostIdSchema>;
export type SourceSnapshotId = Type.Static<typeof SourceSnapshotIdSchema>;
export type SourceConnectionId = Type.Static<typeof SourceConnectionIdSchema>;
export type UploadSessionId = Type.Static<typeof UploadSessionIdSchema>;
export type BuildId = Type.Static<typeof BuildIdSchema>;
export type ArtifactId = Type.Static<typeof ArtifactIdSchema>;
export type ReleaseId = Type.Static<typeof ReleaseIdSchema>;
export type DeploymentId = Type.Static<typeof DeploymentIdSchema>;
export type DomainId = Type.Static<typeof DomainIdSchema>;
export type ProviderIntegrationId = Type.Static<typeof ProviderIntegrationIdSchema>;
export type DomainDnsRecordId = Type.Static<typeof DomainDnsRecordIdSchema>;
export type CaddyRouteRevisionId = Type.Static<typeof CaddyRouteRevisionIdSchema>;
export type DatabaseId = Type.Static<typeof DatabaseIdSchema>;
export type DatabaseGrantEvidenceId = Type.Static<typeof DatabaseGrantEvidenceIdSchema>;
export type BackupArtifactId = Type.Static<typeof BackupArtifactIdSchema>;
export type RestoreAttemptId = Type.Static<typeof RestoreAttemptIdSchema>;
export type SecretBindingId = Type.Static<typeof SecretBindingIdSchema>;
export type SecretId = Type.Static<typeof SecretIdSchema>;
export type ResourceType = Type.Static<typeof ResourceTypeSchema>;
export type ResourceReference = Type.Static<typeof ResourceReferenceSchema>;
